import { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { apiService } from '@/lib/api';
import 'react-day-picker/style.css';

interface RoomAvailabilityViewProps {
  roomId: string;
  monthsToShow?: number;
  variant?: 'inline' | 'dialog';
}

/**
 * Read-only calendar view for guests to see room availability
 * Shows which dates are available, reserved, or blocked
 */
export default function RoomAvailabilityView({
  roomId,
  monthsToShow = 2,
  variant = 'dialog',
}: RoomAvailabilityViewProps) {
  const [loading, setLoading] = useState(false);
  const [blockedDates, setBlockedDates] = useState<Date[]>([]);
  const [reservedDates, setReservedDates] = useState<Date[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Load availability data
  const loadAvailability = async (month: Date) => {
    setLoading(true);
    try {
      const startDate = format(startOfMonth(month), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(addMonths(month, monthsToShow)), 'yyyy-MM-dd');

      const data = await apiService.getRoomAvailabilityCalendar(roomId, startDate, endDate);

      // Parse blocked and reserved dates
      const blocked: Date[] = [];
      const reserved: Date[] = [];

      data.forEach((item: any) => {
        const date = new Date(item.date);
        if (item.status === 'BLOCKED') {
          blocked.push(date);
        } else if (item.status === 'RESERVED') {
          reserved.push(date);
        }
      });

      setBlockedDates(blocked);
      setReservedDates(reserved);
    } catch (error: any) {
      console.error('Failed to load availability:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvailability(currentMonth);
  }, [roomId, currentMonth]);

  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month);
  };

  const modifiers = {
    blocked: blockedDates,
    reserved: reservedDates,
  };

  const modifiersStyles = {
    blocked: {
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: 'bold',
    },
    reserved: {
      backgroundColor: '#c39d76', // Brand primary color
      color: 'white',
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-white border-2 border-gray-300 rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#c39d76' }}></div>
          <span>Reserved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span>Not Available</span>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex justify-center">
        <DayPicker
          mode="default"
          month={currentMonth}
          onMonthChange={handleMonthChange}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          numberOfMonths={monthsToShow}
          disabled={{ before: new Date() }}
        />
      </div>
    </div>
  );
}
